/* Copyright (c) 2020-present CloudIO Inc */

import {
  caseOppositeOperator,
  isOpCaseInsensitive,
  STRING_OPS_KEY_TYPE,
} from '@cloudio-saas/datasource-types';
import { useLatest, useStateXSelector } from '@cloudio-saas/statex';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { OperatorProp } from 'componentregistry';
import {
  AttributeDataType,
  defaultOperators,
} from 'componentregistry/components/SmartSearch';
import { getOpOptions } from 'components/SmartSearch/SearchUtils';
import { helpTextSelector } from 'designer/state';
import useMounted from 'hooks/useMounted';
import Icon from 'icons/Icon';
import React, { memo, useEffect, useState } from 'react';
import { itemWithDefaultValues } from 'registry/Components';
import { useTranslate } from '../../../../../utils/lang';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
});

function DefaultOperatorProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<string>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<OperatorProp, string>) {
  const classes = useStyles();
  const { t } = useTranslate();
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const [dataType, setDataType] = useState<AttributeDataType | null>(null);
  const [state, setState] = useState([]);
  const mounted = useMounted();
  const [caseInSensitive, setCaseInsenstive] = useState(false);
  const [operators, setOperators] = useState<
    {
      value: string;
      label: string;
    }[]
  >([]);

  useEffect(() => {
    if (def.refreshCallback) {
      def.refreshCallback(() => {
        setState([]);
        setOperators([]);
        setDataType(null);
        setCaseInsenstive(false);
      });
    }
  }, [def]);

  const latestCaseInSensitive = useLatest(caseInSensitive);
  const latestValue = useLatest(value);

  useEffect(() => {
    // will run the first time and whenever the attribute is changed
    const page = platform.getPage(appUid, pageId);
    const itemWithDefaults = itemWithDefaultValues(page[activeItemId]);
    def
      .getDataType(itemWithDefaults, {
        appUid,
        itemId: activeItemId,
        pageId,
        mgr,
        platform,
      })
      .then((_dataType) => {
        if (mounted.current) {
          setDataType(_dataType);
          setCaseInsenstive(isOpCaseInsensitive(value as STRING_OPS_KEY_TYPE));
        }
      });
  }, [activeItemId, appUid, def, mgr, mounted, pageId, platform, state, value]);

  useEffect(() => {
    if (dataType) {
      // this will run the first time and everytime the dataType changes
      const _operators = getOpOptions(
        dataType,
        t,
        latestCaseInSensitive.current,
      );
      const op = _operators.find((o) => o.value === latestValue.current);
      if (!op) {
        platform.forceUpdater(() => {
          onChange(
            property,
            latestCaseInSensitive.current
              ? caseOppositeOperator(
                  defaultOperators[dataType] as STRING_OPS_KEY_TYPE,
                )
              : defaultOperators[dataType],
          );
        });
      }
      setOperators(_operators);
    }
  }, [
    dataType,
    latestCaseInSensitive,
    latestValue,
    onChange,
    platform,
    property,
    t,
  ]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const _value: string | undefined = event.target.value as string;
    onChange(property, _value);
  };

  const _handleCaseInsensitiveChange = () => {
    setCaseInsenstive(!caseInSensitive);
    if (dataType) {
      setOperators(getOpOptions(dataType, t, !caseInSensitive));
    }
    if (value) {
      platform.forceUpdater(() => {
        onChange(property, caseOppositeOperator(value as STRING_OPS_KEY_TYPE));
      });
    }
  };
  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  if (!dataType || operators.length === 0) {
    return null;
  }

  return (
    <FormControl
      data-testid={property}
      required={required}
      fullWidth
      variant="standard"
      component="fieldset"
      className={classes.root}
    >
      <InputLabel>{def.label}</InputLabel>
      <Select
        disabled={isViewOnlyUser}
        variant="standard"
        value={value}
        onChange={handleChange}
        style={{ position: 'relative' }}
        endAdornment={
          dataType === 'Text' && !['empty', 'notEmpty'].includes(value) ? (
            <Icon
              disabled={isViewOnlyUser}
              iconName="font-case"
              onClick={_handleCaseInsensitiveChange}
              tooltip={
                caseInSensitive
                  ? t('Ignore Case')
                  : t('Match Case. Click for case insensitive filter')
              }
              parentColor={caseInSensitive ? 'default' : 'primary'}
              sx={{
                position: 'absolute',
                marginTop: '2px',
                right: '24px',
                padding: '4px',
                height: '24px',
                width: '24px',
                backgroundColor: caseInSensitive ? 'default' : 'primary.main',
              }}
            />
          ) : null
        }
      >
        {def.required ? null : <MenuItem value="">None</MenuItem>}
        {operators.map((op) => (
          <MenuItem key={op.value} value={op.value}>
            {op.label}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{helpText}</FormHelperText>
    </FormControl>
  );
}

export default memo(DefaultOperatorProperty);
